import { CheckCircleFilled, SearchOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, Empty, Input, Select, Skeleton, Spin, Tag } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SWAP_SUGGEST_TOYS_ADMIN } from '../../../../graphql/Queries';
import { ROADMAP_SLOTS } from '../../../../ultraPlanConstants';
import {
  compareToysByAge,
  getToyTypeSlot,
  isBigToy,
} from '../../../../ultraPlanUtils';
import ToyAgeTags from './ToyAgeTags';
import ToyThumbnail from './ToyThumbnail';

const PAGE_SIZE = 25;
const SKELETON_ROWS = 4;
const ALL_TYPES = 'ALL';
const TYPE_OPTIONS = [
  { label: 'All types', value: ALL_TYPES },
  ...ROADMAP_SLOTS.map((slot) => ({
    label: slot?.label,
    value: slot?.toyType,
  })),
];

export const getToyLabel = (toy) =>
  toy ? `${toy?.name || '-'} (#${toy?.toyNumber || '-'})` : '-';

const ToyPickerRow = ({ toy, isSelected, disabledReason, onSelect }) => {
  const typeSlot = getToyTypeSlot(toy);
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      className={`toy-picker-row ${isSelected ? 'is-selected' : ''}`}
      disabled={!!disabledReason}
      onClick={onSelect}
    >
      <ToyThumbnail toy={toy} size={40} preview={false} />
      <span className="toy-picker-row-info">
        <span className="toy-picker-row-name" title={getToyLabel(toy)}>
          {toy?.name || '-'}
        </span>
        <span className="roadmap-toy-option-meta">
          {typeSlot && (
            <Tag color={typeSlot?.color} className="roadmap-toy-type-tag">
              {typeSlot?.label}
            </Tag>
          )}
          <ToyAgeTags toy={toy} />
        </span>
        {disabledReason && (
          <span className="toy-picker-row-reason">{disabledReason}</span>
        )}
      </span>
      {isSelected && <CheckCircleFilled className="toy-picker-row-check" />}
    </button>
  );
};

const RoadmapToyPicker = ({
  userId,
  weekNumber,
  value,
  onChange,
  excludeToyId,
  bigToyBlockedReason,
  placeholder = 'Search toy by name or toy number',
}) => {
  const [items, setItems] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [toyType, setToyType] = useState(ALL_TYPES);
  const skipRef = useRef(0);
  const searchRef = useRef('');
  const toyTypeRef = useRef(ALL_TYPES);
  const lastScrollTopRef = useRef(0);
  const requestIdRef = useRef(0);

  const [fetchToys] = useLazyQuery(SWAP_SUGGEST_TOYS_ADMIN, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const fetchPage = ({ skip }) =>
    fetchToys({
      variables: {
        where: { userId, weekNumber },
        filter: {
          skip,
          limit: PAGE_SIZE,
          search: searchRef.current || undefined,
          types:
            toyTypeRef.current === ALL_TYPES ? undefined : [toyTypeRef.current],
        },
      },
    });

  const loadToys = async ({ reset = false } = {}) => {
    if (!userId || !weekNumber) return;
    if (reset) requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    if (reset) setIsInitialLoading(true);
    else setIsLoadingMore(true);

    try {
      const response = await fetchPage({ skip: reset ? 0 : skipRef.current });
      if (requestId !== requestIdRef.current) return;
      if (reset) setHasLoadError(!!response?.error);
      const pageData = response?.data?.swapSuggestToysAdmin?.data || [];
      skipRef.current = (reset ? 0 : skipRef.current) + pageData?.length;
      setHasMore(!response?.error && pageData?.length === PAGE_SIZE);
      const matches = pageData
        ?.filter((item) => item?.toy?.id)
        ?.map((item) => ({ toy: item?.toy }));
      setItems((prevItems) => {
        const merged = reset ? [] : [...prevItems];
        const seenIds = new Set(merged?.map((item) => item?.toy?.id));
        matches
          ?.sort((a, b) => compareToysByAge(a?.toy, b?.toy))
          ?.forEach((item) => {
            if (!seenIds?.has(item?.toy?.id)) {
              seenIds?.add(item?.toy?.id);
              merged?.push(item);
            }
          });
        return merged;
      });
    } finally {
      if (requestId === requestIdRef.current) {
        setIsInitialLoading(false);
        setIsLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    loadToys({ reset: true });
  }, []);

  const debouncedSearch = useMemo(
    () =>
      debounce((search) => {
        searchRef.current = search;
        lastScrollTopRef.current = 0;
        loadToys({ reset: true });
      }, 500),
    [],
  );

  useEffect(() => () => debouncedSearch?.cancel(), [debouncedSearch]);

  const handleTypeChange = (type) => {
    setToyType(type);
    toyTypeRef.current = type;
    lastScrollTopRef.current = 0;
    loadToys({ reset: true });
  };

  const handleListScroll = (event) => {
    const { scrollTop, offsetHeight, scrollHeight } = event?.target || {};
    const isScrollingDown = scrollTop > lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;
    if (
      isScrollingDown &&
      !isInitialLoading &&
      !isLoadingMore &&
      hasMore &&
      scrollTop + offsetHeight >= scrollHeight - 20
    ) {
      loadToys();
    }
  };

  const toys = useMemo(
    () =>
      items
        ?.filter((item) => item?.toy?.id !== excludeToyId)
        ?.map((item) => item?.toy),
    [items, excludeToyId],
  );

  const renderList = () => {
    if (isInitialLoading) {
      return Array.from({ length: SKELETON_ROWS }, (_, index) => (
        <Skeleton
          key={index}
          active
          avatar={{ shape: 'square' }}
          title={false}
          paragraph={{ rows: 2, width: ['60%', '30%'] }}
          className="toy-picker-skeleton"
        />
      ));
    }
    if (hasLoadError) {
      return (
        <Empty
          className="toy-picker-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Couldn't load toys."
        >
          <Button size="small" onClick={() => loadToys({ reset: true })}>
            Retry
          </Button>
        </Empty>
      );
    }
    if (!toys?.length) {
      const typeLabel = TYPE_OPTIONS?.find(
        (option) => option?.value === toyType,
      )?.label;
      let emptyText = 'No toys available for this slot right now.';
      if (searchRef.current) {
        emptyText = 'No matching toys. Try another name or toy number.';
      } else if (toyType !== ALL_TYPES) {
        emptyText = `No ${typeLabel?.toLowerCase()} available right now.`;
      }
      return (
        <Empty
          className="toy-picker-empty"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={emptyText}
        />
      );
    }
    return (
      <>
        {toys?.map((toy) => {
          const isSelected = toy?.id === value;
          return (
            <ToyPickerRow
              key={toy?.id}
              toy={toy}
              isSelected={isSelected}
              disabledReason={isBigToy(toy) ? bigToyBlockedReason : null}
              onSelect={() =>
                isSelected ? onChange?.(null, null) : onChange?.(toy?.id, toy)
              }
            />
          );
        })}
        {isLoadingMore && (
          <div className="toy-picker-loading-more">
            <Spin size="small" />
          </div>
        )}
      </>
    );
  };

  return (
    <div className="toy-picker">
      <div className="toy-picker-controls">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder={placeholder}
          onChange={(event) => debouncedSearch(event?.target?.value?.trim())}
        />
        <Select
          className="toy-picker-type-filter"
          options={TYPE_OPTIONS}
          value={toyType}
          onChange={handleTypeChange}
        />
      </div>
      <div
        className="toy-picker-list"
        role="listbox"
        onScroll={handleListScroll}
      >
        {renderList()}
      </div>
    </div>
  );
};

export default RoadmapToyPicker;
